import React from 'react';
import { useTranslation } from 'react-i18next';

import SunEditor from 'suneditor-react';
import plugins from 'suneditor/src/plugins'
import 'suneditor/dist/css/suneditor.min.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useResponsive } from '../../../../hooks/use-responsive';

function Subsection({ subsectionTitle, subsectionContent, setSubsectionTitle, setSubsectionContent, saveSubsection, edit, open, setOpen }) {
    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');
    const editorRef = React.useRef(null);

    const SE_OPTIONS = {
        font: 'sans-serif',
        resizeEnable: false,
        resizingBar: false,
        height: mdUp ? '50vh' : '40vh',
        maxHeight: mdUp ? '50vh' : '40vh',
        katex: katex,
        plugins: plugins,
        buttonList: [
            [
                'undo', 'redo',
                'fontSize', 'formatBlock',
                'blockquote',
                'bold', 'underline', 'italic', 'strike', 'subscript', 'superscript',
                'fontColor', 'hiliteColor',
                'removeFormat',
                'outdent', 'indent',
                'align', 'list', 'lineHeight',
                'table', 'image', 'link', 'math',
                'codeView', 'fullScreen'
            ]
        ],
        attributesWhitelist: {
            all: 'class,style,src,alt,width,height'
        }
    };

    const getSunEditorInstance = (sunEditor) => {
        editorRef.current = sunEditor;
    }

    const save = async () => {
        const editor = editorRef.current;
        if (!editor) {
            try {
                await saveSubsection(subsectionContent);
            } catch (err) {
                console.error('Unable to save subsection (fallback)', err);
            }
            return;
        }

        const core = editor.core;
        let content = '';

        // Detect Code View mode (multiple strategies to be resilient across versions)
        const wysiwyg = core?.context?.element?.wysiwyg;
        const wysiwygCode = core?.context?.element?.wysiwygCode;
        const styleBasedCodeView =
            wysiwyg?.style?.display === 'none' && wysiwygCode?.style?.display !== 'none';
        const flagBasedCodeView = !!core?.hasCodeView;
        const isCodeView = styleBasedCodeView || flagBasedCodeView;

        if (isCodeView) {
            // Try to read the code textarea from SunEditor's internal context
            let codeTextarea = null;
            const codeContainer = core?.context?.element?.code;
            if (codeContainer && typeof codeContainer.querySelector === 'function') {
                codeTextarea = codeContainer.querySelector('textarea');
            }

            // Fallback: query common selectors in DOM
            if (!codeTextarea) {
                codeTextarea = document.querySelector(
                    '.sun-editor .se-code textarea, .sun-editor .se-input-code, .sun-editor-editable-area textarea'
                );
            }
            if (codeTextarea && typeof codeTextarea.value === 'string') {
                content = codeTextarea.value;
            }

            // Final fallback: temporarily toggle out of code view to sync, read, and toggle back
            if (!content || content.length === 0) {
                let toggled = false;
                try {
                    if (typeof core.toggleCodeView === 'function') {
                        core.toggleCodeView();
                        toggled = true;
                    }
                    content = editor.getContents() || '';
                } catch (e) {
                    console.error('Unable to save subsection (final fallback)', e);
                } finally {
                    if (toggled && typeof core.toggleCodeView === 'function') {
                        try { core.toggleCodeView(); } catch (_) { }
                    }
                }
            }
        } else {
            content = editor.getContents() || '';
        }

        setSubsectionContent(content.trim());

        try {
            await saveSubsection(content.trim());
        } catch (err) {
            console.error('Unable to save subsection', err);
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    padding: '24px',
                    minWidth: { xs: '95%', md: '51%' },
                    maxWidth: { xs: '95%', md: '51%' },
                }}
            >
                <Typography variant='h6' sx={{ mb: 4 }}>
                    {edit ? t('course.modals.newSubsection.edit') : t('course.modals.newSubsection.title')}
                </Typography>
                <Stack spacing={4}>
                    <TextField
                        id='mISLec_subsection_title'
                        label={t('course.modals.newSubsection.input')}
                        variant='outlined'
                        value={subsectionTitle ? subsectionTitle : ''}
                        onChange={(e) => setSubsectionTitle(e.target.value)}
                        fullWidth
                    />
                    <SunEditor
                        getSunEditorInstance={getSunEditorInstance}
                        setOptions={SE_OPTIONS}
                        value={subsectionContent}
                        defaultValue={subsectionContent}
                        onChange={(e) => setSubsectionContent(e)}
                    />
                </Stack>
                <Stack
                    direction='row'
                    alignItems='center'
                    mt={4}
                    sx={{
                        justifyContent: {
                            xs: 'space-between',
                            md: 'flex-end'
                        }
                    }}
                >
                    <Button
                        variant=''
                        onClick={() => setOpen(false)}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.error.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'error.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'error.main',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                            }
                        }}
                    >
                        {t('course.modals.newSubsection.cancel')}
                    </Button>
                    <Button
                        onClick={() => save()}
                        sx={{
                            ml: {
                                xs: 0,
                                md: 1
                            },
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.primary.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'primary.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        {edit ? t('course.modals.newSubsection.update') : t('course.modals.newSubsection.create')}
                    </Button>
                </Stack>
            </Paper>
        </Modal>
    );
}

export default Subsection;